import React, { useState } from 'react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Page de changement de mot de passe FORCÉ
// ─────────────────────────────────────────────────────────────────────────────
// Affichée quand `user.doit_changer_mot_de_passe === true` (compte créé ou
// réinitialisé par un admin). L'utilisateur ne peut accéder à AUCUNE autre
// page tant qu'il n'a pas choisi son propre mot de passe.
// ─────────────────────────────────────────────────────────────────────────────

export default function ChangerMotDePasseForce() {
  const { user, logout, refreshUser } = useAuth();
  const [form, setForm] = useState({ ancien: '', nouveau: '', confirm: '' });
  const [voir, setVoir] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');

    if (form.nouveau.length < 6) {
      return setErreur('Le mot de passe doit faire au moins 6 caractères');
    }
    if (form.nouveau !== form.confirm) {
      return setErreur('Les deux saisies ne correspondent pas');
    }
    if (form.nouveau === form.ancien) {
      return setErreur('Le nouveau mot de passe doit être différent de l\'ancien');
    }

    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        ancienMotDePasse: form.ancien,
        nouveauMotDePasse: form.nouveau,
      });
      toast.success('Mot de passe défini avec succès — bienvenue !');
      // Rafraîchir le user pour récupérer le flag à FALSE et débloquer l'app
      if (refreshUser) await refreshUser();
      else window.location.reload();
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors du changement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <ShieldAlert className="text-yellow-400" size={22} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Définis ton mot de passe</h1>
            <p className="text-xs text-gray-400">Étape obligatoire avant d'accéder à l'application</p>
          </div>
        </div>

        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3 text-xs text-yellow-200/90 mb-4">
          Ton compte vient d'être créé ou réinitialisé par un administrateur.
          Le mot de passe qu'il t'a communiqué est temporaire. Choisis-en un
          que <strong>toi seul</strong> connais pour sécuriser ton accès.
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {erreur && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300">
              {erreur}
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1">Mot de passe temporaire reçu</label>
            <input
              type={voir ? 'text' : 'password'}
              value={form.ancien}
              onChange={e => setForm(f => ({ ...f, ancien: e.target.value }))}
              autoFocus
              required
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Nouveau mot de passe (min 6 caractères)</label>
            <input
              type={voir ? 'text' : 'password'}
              value={form.nouveau}
              onChange={e => setForm(f => ({ ...f, nouveau: e.target.value }))}
              required
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Confirme le nouveau mot de passe</label>
            <input
              type={voir ? 'text' : 'password'}
              value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              required
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={voir}
              onChange={e => setVoir(e.target.checked)}
              className="w-3.5 h-3.5 rounded bg-gray-800 border-gray-700 text-yellow-500 focus:ring-yellow-500"
            />
            Afficher les mots de passe
          </label>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 text-black font-bold rounded-xl flex items-center justify-center gap-2"
          >
            {saving
              ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              : <><KeyRound size={16} /> Définir et accéder à l'app</>}
          </button>

          <button
            type="button"
            onClick={logout}
            className="w-full text-xs text-gray-500 hover:text-gray-300 pt-2"
          >
            Me déconnecter (je le ferai plus tard)
          </button>
        </form>

        {user && (
          <p className="text-[10px] text-gray-600 text-center mt-3">
            Compte : {user.email}
          </p>
        )}
      </div>
    </div>
  );
}
